import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {
    trigger,
    state,
    style,
    transition,
    animate
} from '@angular/animations';
import { SportResultFormatterService } from '../../services/sport-result-formatter.service';
import { TimeZoneService } from '../../services/time-zone.service';

@Component({
    selector: 'app-ticket-details',
    templateUrl: './ticket-details.component.html',
    styleUrls: ['./ticket-details.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition(
                'expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            )
        ])
    ]
})
export class TicketDetailsComponent implements OnInit, OnChanges {
    @Input() isExpanded: boolean = false;
    @Input() ticketDetails: any = null;
    @Input() ticketRow: any = null;
    @Input() loading: boolean = false;
    @Input() error: string | null = null;
    @Output() onRetry = new EventEmitter<void>();

    parsedTicketDetails: any = null;
    parsedSelections: any[] = [];
    ticketIsWon: number | null = null;
    currentTimeZone: string = 'Europe/Belgrade';

    constructor(
        private sportFormatter: SportResultFormatterService,
        private timeZoneService: TimeZoneService
    ) {}

    ngOnInit(): void {
        this.parseTicketDetails();

        // Subscribe to time zone changes to update currentTimeZone
        this.timeZoneService.selectedTimeZone$.subscribe(
            (tz) => (this.currentTimeZone = tz)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ticketDetails'] || changes['ticketRow']) {
            this.parseTicketDetails();
        }
    }

    parseTicketDetails(): void {
        if (!this.ticketDetails && !this.ticketRow) return;

        try {
            let parsed: any = null;

            // Check if ticketRow has ticketDetails as a string
            if (this.ticketRow?.ticketDetails) {
                parsed =
                    typeof this.ticketRow.ticketDetails === 'string'
                        ? JSON.parse(this.ticketRow.ticketDetails)
                        : this.ticketRow.ticketDetails;
            } else if (this.ticketDetails) {
                // Fallback to direct ticketDetails
                parsed =
                    typeof this.ticketDetails === 'string'
                        ? JSON.parse(this.ticketDetails)
                        : this.ticketDetails;
            }

            if (!parsed) return;

            this.parsedTicketDetails = parsed;

            // Parse selections from JSON string
            if (parsed.selections) {
                this.parsedSelections =
                    typeof parsed.selections === 'string'
                        ? JSON.parse(parsed.selections)
                        : parsed.selections;
            }

            // Get ticket win status from ticketRow.isWon first, then simulations
            if (
                this.ticketRow?.isWon !== undefined &&
                this.ticketRow?.isWon !== null
            ) {
                this.ticketIsWon = this.ticketRow.isWon;
            } else if (parsed.simulations && parsed.simulations.length > 0) {
                // For multi-selection tickets, all simulations must be won
                this.ticketIsWon = parsed.simulations.every(
                    (sim: any) => sim.isWon === 1
                )
                    ? 1
                    : 0;
            }

            // Map selection win status and result from simulations
            if (parsed.simulations && this.parsedSelections.length > 0) {
                this.parsedSelections = this.parsedSelections.map(
                    (selection: any) => {
                        const simulation = parsed.simulations.find(
                            (sim: any) => sim.EID === selection.EID
                        );

                        // Use new sport formatter service
                        const isFootball =
                            selection.S === 'F' ||
                            selection.S?.toUpperCase() === 'FOOTBALL';
                        const formattedResult = simulation
                            ? this.sportFormatter.formatResult(
                                  simulation,
                                  isFootball
                              )
                            : { score: null, result: null, goals: null };

                        return {
                            ...selection,
                            isWon: simulation ? simulation.isWon : 0,
                            formattedScore: formattedResult.score,
                            formattedDetails: formattedResult.result,
                            formattedGoals: formattedResult.goals,
                            // Keep legacy fields for backward compatibility
                            score: formattedResult.score,
                            result: formattedResult.result,
                            home: selection.HN,
                            away: selection.AN,
                            sport: selection.S,
                            market: selection.MN || selection.MC,
                            selection: selection.SN || selection.SC,
                            ODD: selection.ODD
                        };
                    }
                );
            }
        } catch (error) {
            console.error('Error parsing ticket details:', error, {
                ticketDetails: this.ticketDetails,
                ticketRow: this.ticketRow
            });
        }
    }

    trackBySelection(index: number, selection: any): any {
        return selection.matchUuid || index;
    }

    getSportName(sport: string): string {
        const sportNames: { [key: string]: string } = {
            F: 'Football',
            B: 'Basketball',
            T: 'Tennis',
            H: 'Hockey',
            V: 'Volleyball',
            A: 'American Football',
            R: 'Rugby',
            C: 'Cricket',
            S: 'Snooker',
            D: 'Darts',
            E: 'Esports',
            O: 'Other'
        };
        return sportNames[sport] || sport || 'Unknown';
    }

    getMarketName(market: string): string {
        const marketNames: { [key: string]: string } = {
            '1X2': '1X2',
            OU: 'Over/Under',
            AH: 'Asian Handicap',
            DC: 'Double Chance',
            BTTS: 'Both Teams To Score',
            CS: 'Correct Score',
            HT: 'Half Time',
            FT: 'Full Time'
        };
        return marketNames[market] || market || 'Unknown Market';
    }

    getSelectionName(selection: string): string {
        const selectionNames: { [key: string]: string } = {
            '1': 'Home Win',
            X: 'Draw',
            '2': 'Away Win',
            O: 'Over',
            U: 'Under',
            Y: 'Yes',
            N: 'No',
            '1X': 'Home or Draw',
            '12': 'Home or Away',
            X2: 'Draw or Away'
        };
        return selectionNames[selection] || selection || 'Unknown Selection';
    }

    getSelectionBend(selectionData: any): string | null {
        // Proveri da li postoji B property
        if (selectionData?.B === undefined || selectionData?.B === null) {
            return null;
        }

        const band = selectionData.B;

        // Niz od vise bendova (kad ima 2 benda na marketu)
        if (Array.isArray(band)) {
            return band.join(' / ');
        }

        // Broj - formatuj sa + za pozitivne vrednosti
        if (typeof band === 'number') {
            return band > 0 ? `+${band}` : `${band}`;
        }

        // String - samo validni bendovi (sa decimalnom tackom ili +/- znakom)
        if (typeof band === 'string') {
            const trimmed = band.trim();
            // Ignorisi prazne stringove i obicne brojeve bez decimale ili znaka (npr "1", "2")
            if (trimmed && /^[+-]?\d*\.?\d+$/.test(trimmed)) {
                const hasDecimal = trimmed.includes('.');
                const hasSign = trimmed.includes('+') || trimmed.includes('-');
                if (hasDecimal || hasSign) {
                    return trimmed;
                }
            }
        }

        return null;
    }

    calculateTotalOdds(): number {
        if (!this.parsedSelections || this.parsedSelections.length === 0)
            return 0;

        return this.parsedSelections.reduce((total: number, selection: any) => {
            const odd = parseFloat(selection.ODD || selection.odd || '1');
            return total * odd;
        }, 1);
    }

    formatCurrencyWithSpace(amount: number, currency: string): string {
        if (!amount || !currency) return '/';

        // Format the number with 2 decimal places
        const formattedAmount = amount.toFixed(2);

        // Add space between currency and amount
        return `${currency} ${formattedAmount}`;
    }

    copyTicketId(): void {
        if (this.ticketRow?.ticketUuid) {
            navigator.clipboard
                .writeText(this.ticketRow.ticketUuid)
                .then(() => {
                    // Optional: Add a toast notification or visual feedback
                    console.log('Ticket ID copied to clipboard');
                })
                .catch((err) => {
                    console.error('Failed to copy ticket ID: ', err);
                    // Fallback for older browsers
                    this.fallbackCopyTextToClipboard(this.ticketRow.ticketUuid);
                });
        }
    }

    private fallbackCopyTextToClipboard(text: string): void {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand('copy');
            console.log('Ticket ID copied to clipboard (fallback)');
        } catch (err) {
            console.error('Fallback: Could not copy text: ', err);
        }

        document.body.removeChild(textArea);
    }
}
